import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Crown, Shield, UsersRound, Search, Building2, Power, PowerOff, Trash2, Plus, Mail, X, Link2, Copy, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super-Admin",
  hospital_admin: "Admin hôpital",
  doctor: "Médecin",
  nurse: "Infirmier(e)",
  caregiver: "Aide-soignant(e)",
  secretary: "Secrétaire",
};
const ROLE_COLORS = {
  super_admin: "#D96C4A",
  hospital_admin: "#1E4631",
  doctor: "#D96C4A",
  nurse: "#2E7D32",
  caregiver: "#5B8DB8",
  secretary: "#E3A72F",
};

export default function SuperStaff() {
  const { user } = useApp();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", name: "",
    commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC",
  });
  const [showInviteAddress, setShowInviteAddress] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/users`);
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (u) => {
    const action = u.active ? "désactiver" : "réactiver";
    if (!window.confirm(`${action.charAt(0).toUpperCase()+action.slice(1)} ${u.name} (${u.email}) ?`)) return;
    try {
      await axios.patch(`${API}/users/${u.id}/status?active=${!u.active}`);
      toast.success(`Compte ${action.replace('é', 'é')}é`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.name} (${u.email}) ?`)) return;
    try {
      await axios.delete(`${API}/staff/${u.id}`);
      toast.success("Membre supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/staff/invite`, {
        email: inviteForm.email,
        name: inviteForm.name,
        role: "super_admin",
        commune: inviteForm.commune, quartier: inviteForm.quartier,
        avenue: inviteForm.avenue, ville: inviteForm.ville, pays: inviteForm.pays,
      });
      setInviteResult(r.data);
      if (r.data.delivered) toast.success(`Invitation envoyée à ${inviteForm.email}`);
      else toast.info("Invitation créée — partagez le lien manuellement");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const closeInvite = () => {
    setInviteOpen(false);
    setInviteForm({ email: "", name: "", commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC" });
    setInviteResult(null);
    setShowInviteAddress(false);
  };

  const copyInviteUrl = () => {
    if (!inviteResult?.invite_url) return;
    navigator.clipboard.writeText(inviteResult.invite_url);
    toast.success("Lien copié");
  };

  const filtered = users.filter((u) => {
    if (filterRole !== "all" && u.role !== filterRole) return false;
    if (filterStatus === "active" && !u.active) return false;
    if (filterStatus === "inactive" && u.active) return false;
    if (search) {
      const s = search.toLowerCase();
      return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || (u.hospital_name || "").toLowerCase().includes(s);
    }
    return true;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.active).length,
    kwikila: users.filter(u => u.role === "super_admin").length,
    hospital_staff: users.filter(u => u.role !== "super_admin").length,
  };

  return (
    <div className="space-y-6" data-testid="super-staff-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-[Outfit] font-bold inline-flex items-center gap-3">
            <UsersRound className="w-7 h-7 text-[#D96C4A]"/> Équipes globales
          </h1>
          <p className="text-[#5C6B61] mt-1 text-sm">Vue d'ensemble des utilisateurs de tous les hôpitaux + équipe KWIKILA Care</p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={(v)=>{ if(!v) closeInvite(); else setInviteOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D96C4A] hover:bg-[#B85A3D] text-white" data-testid="invite-super-btn">
              <Plus className="w-4 h-4 mr-2"/> Inviter un super-admin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Inviter un membre équipe KWIKILA Care</DialogTitle></DialogHeader>
            {inviteResult ? (
              <div className="space-y-4" data-testid="invite-result">
                {inviteResult.delivered ? (
                  <div className="p-5 rounded-xl bg-[#2E7D32]/10 border border-[#2E7D32]/30 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-[#2E7D32] mb-1">
                      <Mail className="w-4 h-4"/> Email envoyé !
                    </div>
                    <p className="text-[#5C6B61]">{inviteResult.message} Le destinataire recevra un lien pour activer son compte (valide 7 jours).</p>
                  </div>
                ) : (
                  <div className="p-5 rounded-xl bg-[#E3A72F]/10 border border-[#E3A72F]/30 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-[#1A1D1A] mb-2">
                      <Link2 className="w-4 h-4 text-[#E3A72F]"/> Partagez le lien manuellement
                    </div>
                    <p className="text-[#5C6B61] mb-3">{inviteResult.message}</p>
                    {inviteResult.invite_url && (
                      <div className="flex gap-2">
                        <Input value={inviteResult.invite_url} readOnly className="font-mono text-xs"/>
                        <Button type="button" variant="outline" size="sm" onClick={copyInviteUrl}><Copy className="w-4 h-4"/></Button>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={closeInvite} className="bg-[#1E4631] hover:bg-[#163424]">Fermer</Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={submitInvite} className="space-y-4" data-testid="super-invite-form">
                <div className="p-4 rounded-lg bg-[#D96C4A]/10 border border-[#D96C4A]/20 text-sm">
                  <strong className="text-[#D96C4A]">⭐ Super-Admin équipe KWIKILA</strong>
                  <p className="text-[#5C6B61] text-xs mt-1">Ce membre aura accès à TOUS les hôpitaux et pourra gérer les utilisateurs — sauf le fondateur. Idéal pour co-fondateur, CTO, community manager.</p>
                </div>
                <div>
                  <Label>Nom complet</Label>
                  <Input required value={inviteForm.name} onChange={(e)=>setInviteForm({...inviteForm, name: e.target.value})} data-testid="super-invite-name"/>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" required value={inviteForm.email} onChange={(e)=>setInviteForm({...inviteForm, email: e.target.value})} data-testid="super-invite-email"/>
                </div>

                {/* Adresse pliable */}
                <div className="border border-[#E5EBE7] rounded-lg overflow-hidden">
                  <button type="button" onClick={()=>setShowInviteAddress(v=>!v)}
                          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-[#FDFBF7] transition"
                          data-testid="super-invite-toggle-address">
                    <span className="inline-flex items-center gap-2 text-[#1A1D1A]">
                      <MapPin className="w-4 h-4 text-[#D96C4A]"/>
                      Adresse physique <span className="text-[#5C6B61] font-normal">(optionnel)</span>
                    </span>
                    {showInviteAddress ? <ChevronUp className="w-4 h-4 text-[#5C6B61]"/> : <ChevronDown className="w-4 h-4 text-[#5C6B61]"/>}
                  </button>
                  {showInviteAddress && (
                    <div className="p-4 grid grid-cols-2 gap-3 border-t border-[#E5EBE7] bg-[#FDFBF7]/40">
                      <div><Label className="text-xs">Commune</Label><Input value={inviteForm.commune} onChange={(e)=>setInviteForm({...inviteForm, commune: e.target.value})} placeholder="Gombe…"/></div>
                      <div><Label className="text-xs">Quartier</Label><Input value={inviteForm.quartier} onChange={(e)=>setInviteForm({...inviteForm, quartier: e.target.value})}/></div>
                      <div className="col-span-2"><Label className="text-xs">Avenue et N°</Label><Input value={inviteForm.avenue} onChange={(e)=>setInviteForm({...inviteForm, avenue: e.target.value})}/></div>
                      <div><Label className="text-xs">Ville</Label><Input value={inviteForm.ville} onChange={(e)=>setInviteForm({...inviteForm, ville: e.target.value})}/></div>
                      <div><Label className="text-xs">Pays</Label><Input value={inviteForm.pays} onChange={(e)=>setInviteForm({...inviteForm, pays: e.target.value})}/></div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeInvite}>Annuler</Button>
                  <Button type="submit" className="bg-[#D96C4A] hover:bg-[#B85A3D]" data-testid="super-invite-submit">
                    <Mail className="w-4 h-4 mr-2"/> Envoyer l'invitation
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Utilisateurs totaux" value={stats.total} icon={UsersRound} color="#1E4631"/>
        <StatCard label="Actifs" value={stats.active} icon={Power} color="#2E7D32"/>
        <StatCard label="Équipe KWIKILA" value={stats.kwikila} icon={Shield} color="#D96C4A"/>
        <StatCard label="Staff hôpitaux" value={stats.hospital_staff} icon={Building2} color="#E3A72F"/>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border border-[#E5EBE7]">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B61]"/>
          <Input placeholder="Rechercher nom, email, hôpital…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" data-testid="staff-search"/>
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-44" data-testid="filter-role"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            <SelectItem value="super_admin">Super-Admin</SelectItem>
            <SelectItem value="hospital_admin">Admin hôpital</SelectItem>
            <SelectItem value="doctor">Médecin</SelectItem>
            <SelectItem value="nurse">Infirmier(e)</SelectItem>
            <SelectItem value="caregiver">Aide-soignant(e)</SelectItem>
            <SelectItem value="secretary">Secrétaire</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="inactive">Désactivés</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#5C6B61] ml-auto">{filtered.length} résultat(s)</span>
      </div>

      <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61] tracking-wider">
            <tr>
              <th className="px-5 py-3">Nom</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Rôle</th>
              <th className="px-5 py-3">Hôpital</th>
              <th className="px-5 py-3">Statut</th>
              <th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-[#5C6B61]" data-testid="empty-staff">Aucun utilisateur</td></tr>
            ) : filtered.map((u) => {
              const isFounder = u.is_founder;
              const isSelf = u.id === user.id;
              const canManage = !isFounder && !isSelf;
              return (
                <tr key={u.id} className={`border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50 ${!u.active ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {isFounder && <Crown className="w-4 h-4 text-[#E3A72F]" title="Fondateur"/>}
                      {u.name}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#5C6B61]">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: `${ROLE_COLORS[u.role]}15`, color: ROLE_COLORS[u.role] }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#5C6B61]">{u.hospital_name || "—"}</td>
                  <td className="px-5 py-3">
                    {u.active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2E7D32]">
                        <Power className="w-3 h-3"/> Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#C62828]">
                        <PowerOff className="w-3 h-3"/> Désactivé
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {canManage ? (
                      <div className="flex gap-1.5">
                        <button onClick={()=>toggleStatus(u)}
                                className={`p-1.5 rounded transition ${u.active ? "text-[#E3A72F] hover:bg-[#E3A72F]/10" : "text-[#2E7D32] hover:bg-[#2E7D32]/10"}`}
                                title={u.active ? "Désactiver" : "Réactiver"}
                                data-testid={`toggle-${u.id}`}>
                          {u.active ? <PowerOff className="w-4 h-4"/> : <Power className="w-4 h-4"/>}
                        </button>
                        <button onClick={()=>onDelete(u)}
                                className="text-[#C62828] hover:bg-red-50 p-1.5 rounded transition"
                                title="Supprimer définitivement"
                                data-testid={`delete-${u.id}`}>
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[#5C6B61] italic">{isFounder ? "Protégé" : "Vous-même"}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5">
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5"/>
      </div>
      <div className="mt-3 text-2xl font-[Outfit] font-bold">{value}</div>
      <div className="text-xs text-[#5C6B61] mt-0.5">{label}</div>
    </div>
  );
}
